/**
 * Opportunity Engine Component
 * Full-featured opportunity management with MACC, AI narratives, and PDF export
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Lightbulb, TrendingUp, DollarSign, Leaf, Target, Calculator, Plus, Download,
  ChevronDown, ChevronRight, Sparkles, Info, RefreshCw, FileText, Printer,
  AlertTriangle, CheckCircle, Clock, BarChart3, Zap, Flame, Droplets
} from 'lucide-react';
import { generateBrandedPdf } from '../../utils/pdfBranding';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OpportunityEngine({ study }) {
  const reportRef = useRef(null);
  const [opportunities, setOpportunities] = useState([]);
  const [macc, setMacc] = useState(null);
  const [summary, setSummary] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedSections, setExpandedSections] = useState({
    opportunities: true,
    macc: false,
    narrative: false
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!study?.id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch opportunities
      const oppRes = await axios.get(`${API}/api/ers/opportunities/${study.id}`, config);
      setOpportunities(oppRes.data || []);
      
      // Fetch summary
      const summaryRes = await axios.get(`${API}/api/ers/opportunities/${study.id}/summary`, config);
      setSummary(summaryRes.data);
      
      // Fetch MACC
      const maccRes = await axios.get(`${API}/api/ers/opportunities/${study.id}/macc`, config);
      setMacc(maccRes.data);
      
      // Fetch saved narrative
      try {
        const narrativeRes = await axios.get(`${API}/api/ers/opportunities/${study.id}/narrative`, config);
        if (narrativeRes.data?.narrative) {
          setNarrative(narrativeRes.data);
        }
      } catch (e) {
        console.log('No saved narrative');
      }
    } catch (error) {
      console.error('Error fetching opportunity data:', error);
    } finally {
      setLoading(false);
    }
  }, [study?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate opportunities from SEU and on-site data
  const generateOpportunities = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.post(`${API}/api/ers/opportunities/${study.id}/generate`, {}, config);
      
      if (res.data.opportunities) {
        setOpportunities(res.data.opportunities);
        alert(`Generated ${res.data.opportunities.length} opportunities from ${res.data.sources.seus_analyzed} SEUs, ${res.data.sources.electrical_measurements} electrical measurements, and ${res.data.sources.observations} observations.`);
      }
      
      // Refresh all data
      await fetchData();
    } catch (error) {
      console.error('Error generating opportunities:', error);
      alert('Failed to generate opportunities. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Generate AI narrative
  const generateNarrative = async () => {
    setGeneratingNarrative(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.post(`${API}/api/ers/opportunities/${study.id}/generate-narrative`, {}, config);
      setNarrative(res.data);
    } catch (error) {
      console.error('Error generating narrative:', error);
      alert('Failed to generate AI narrative. Please try again.');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  // Download PDF
  const downloadPDF = async () => {
    if (!reportRef.current) return;

    setDownloadingPDF(true);
    try {
      await generateBrandedPdf({
        element: reportRef.current,
        filename: `Opportunity_Report_${(study?.study_name || study?.name || 'Study').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        reportTitle: 'Opportunity Engine Report',
        subtitle: study?.study_name || study?.name || '',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Print report
  const printReport = () => {
    if (!reportRef.current) return;
    const printContent = reportRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Opportunity Engine Report - ${study?.name || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: 600; }
            h1 { font-size: 20px; color: #1e40af; }
            h2 { font-size: 16px; color: #1e40af; margin-top: 20px; }
            h3 { font-size: 14px; color: #374151; margin-top: 15px; }
            .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
            .logo { max-height: 40px; margin-bottom: 10px; }
            .footer { text-align: center; font-size: 9px; color: #666; border-top: 1px solid #ddd; padding-top: 15px; margin-top: 30px; }
            .summary-box { background: #f0f9ff; padding: 10px; border-radius: 5px; margin: 10px 0; }
            .no-regret { background: #d1fae5; }
            .low-cost { background: #fef3c7; }
            .high-cost { background: #fee2e2; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Filter opportunities
  const filteredOpportunities = opportunities.filter(opp => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'no-capex') return opp.opportunity_category === 'No-capex';
    if (activeFilter === 'low-capex') return opp.opportunity_category === 'Low-capex';
    if (activeFilter === 'capex') return opp.opportunity_category === 'Capex';
    if (activeFilter === 'quick-wins') return opp.simple_payback_months < 12;
    return true;
  });

  // Format narrative text
  const formatNarrative = (text) => {
    if (!text) return null;

    // Helper: convert inline markdown (**bold**) to JSX
    const renderInline = (str) => {
      const parts = str.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    // Table detection helpers
    const isTableRow = (line) => {
      const t = line.trim();
      if (!t.includes('|')) return false;
      const cells = t.replace(/^\|/, '').replace(/\|$/, '').split('|');
      return cells.length >= 2;
    };
    const isSeparatorRow = (line) => {
      const t = line.trim().replace(/^\|/, '').replace(/\|$/, '');
      return /^[\s\-:+|]+$/.test(t) && t.includes('-');
    };
    const parseRow = (row) => {
      const cleaned = row.replace(/^\|/, '').replace(/\|$/, '');
      return cleaned.split('|').map(c => c.trim());
    };

    const lines = text.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const trimmed = lines[i].trim();

      // Empty line
      if (!trimmed) { elements.push(<br key={i} />); i++; continue; }

      // Headings: # ## ### ####
      if (trimmed.startsWith('#')) {
        const level = (trimmed.match(/^#+/) || [''])[0].length;
        const headingText = trimmed.replace(/^#+\s*/, '');
        if (level <= 2) {
          elements.push(<h2 key={i} style={{ wordSpacing: '0.15em' }} className="text-xl font-bold text-indigo-700 mt-5 mb-2">{renderInline(headingText)}</h2>);
        } else if (level === 3) {
          elements.push(<h3 key={i} style={{ wordSpacing: '0.15em' }} className="text-lg font-bold text-gray-900 mt-4 mb-2">{renderInline(headingText)}</h3>);
        } else {
          elements.push(<h4 key={i} style={{ wordSpacing: '0.15em' }} className="text-md font-semibold text-gray-800 mt-3 mb-1">{renderInline(headingText)}</h4>);
        }
        i++; continue;
      }

      // Standalone bold line (acts as sub-heading): **Some Title**
      if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.indexOf('**', 2) === trimmed.length - 2) {
        elements.push(<h3 key={i} style={{ wordSpacing: '0.15em' }} className="text-base font-bold text-gray-900 mt-4 mb-2">{trimmed.slice(2, -2)}</h3>);
        i++; continue;
      }

      // Table block: detect rows containing | with content on both sides
      if (isTableRow(trimmed) && !isSeparatorRow(trimmed)) {
        const tableRows = [];
        while (i < lines.length && (isTableRow(lines[i].trim()) || isSeparatorRow(lines[i].trim()))) {
          tableRows.push(lines[i].trim());
          i++;
        }
        // Separate header, separator, and body
        const dataRows = tableRows.filter(r => !isSeparatorRow(r));
        if (dataRows.length > 0) {
          const headerCells = parseRow(dataRows[0]);
          const bodyRows = dataRows.slice(1);
          elements.push(
            <div key={`table-${i}`} className="overflow-x-auto my-3">
              <table className="w-full text-sm border-collapse border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    {headerCells.map((cell, ci) => (
                      <th key={ci} className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">{renderInline(cell)}</th>
                    ))}
                  </tr>
                </thead>
                {bodyRows.length > 0 && (
                  <tbody>
                    {bodyRows.map((row, ri) => {
                      const cells = parseRow(row);
                      return (
                        <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {cells.map((cell, ci) => (
                            <td key={ci} className="border border-gray-300 px-3 py-2 text-gray-700">{renderInline(cell)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>
            </div>
          );
          continue;
        }
        continue;
      }

      // Bullet list: collect consecutive - or • or * lines
      if (trimmed.startsWith('-') || trimmed.startsWith('•') || (/^\*\s/.test(trimmed))) {
        const items = [];
        while (i < lines.length) {
          const t = lines[i].trim();
          if (t.startsWith('-') || t.startsWith('•') || (/^\*\s/.test(t))) {
            items.push(t.replace(/^[-•*]\s*/, ''));
            i++;
          } else break;
        }
        elements.push(
          <ul key={`ul-${i}`} className="list-disc ml-6 mb-3 space-y-1">
            {items.map((item, li) => (
              <li key={li} className="text-gray-700">{renderInline(item)}</li>
            ))}
          </ul>
        );
        continue;
      }

      // Numbered list: collect consecutive 1. 2. 3. lines
      if (/^\d+[.)]\s/.test(trimmed)) {
        const items = [];
        while (i < lines.length) {
          const t = lines[i].trim();
          if (/^\d+[.)]\s/.test(t)) {
            items.push(t.replace(/^\d+[.)]\s*/, ''));
            i++;
          } else break;
        }
        elements.push(
          <ol key={`ol-${i}`} className="list-decimal ml-6 mb-3 space-y-1">
            {items.map((item, li) => (
              <li key={li} className="text-gray-700">{renderInline(item)}</li>
            ))}
          </ol>
        );
        continue;
      }

      // Horizontal rule
      if (trimmed.match(/^[-*_]{3,}$/)) { elements.push(<hr key={i} className="my-4 border-gray-300" />); i++; continue; }

      // Regular paragraph
      elements.push(<p key={i} className="text-gray-700 mb-2">{renderInline(trimmed)}</p>);
      i++;
    }

    return elements;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600">Loading opportunities...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Opportunity Engine</h2>
            <p className="text-green-100">
              Identify, quantify, and prioritize energy efficiency opportunities with ROI analysis and AI-powered recommendations.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={generateOpportunities}
              disabled={generating}
              className="px-4 py-2.5 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? 'Generating...' : 'Auto-Generate'}
            </button>
            <button
              onClick={() => setShowReport(true)}
              disabled={opportunities.length === 0}
              className="px-4 py-2.5 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{summary?.total_opportunities || 0}</p>
              <p className="text-xs text-green-600">Opportunities</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">₹{((summary?.total_annual_savings || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-blue-600">Annual Savings</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">₹{((summary?.total_capex || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-amber-600">Total CAPEX</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Leaf className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{(summary?.total_co2_reduction || 0).toFixed(0)}</p>
              <p className="text-xs text-purple-600">tCO₂e/yr Reduction</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl p-4 border border-cyan-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Clock className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-700">{summary?.average_payback || 0}</p>
              <p className="text-xs text-cyan-600">Avg Payback (mo)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Register Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('opportunities')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Opportunity Register</h2>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              {opportunities.length} identified
            </span>
          </div>
          {expandedSections.opportunities ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.opportunities && (
          <div className="p-6">
            {/* Filter Row */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['all', 'no-capex', 'low-capex', 'capex', 'quick-wins'].map(filter => (
                <button 
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activeFilter === filter 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter === 'all' ? 'All' : 
                   filter === 'no-capex' ? 'No-CAPEX' :
                   filter === 'low-capex' ? 'Low-CAPEX' :
                   filter === 'capex' ? 'CAPEX' :
                   'Quick Wins (<12mo)'}
                </button>
              ))}
            </div>

            {filteredOpportunities.length === 0 ? (
              <div className="text-center py-8">
                <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No opportunities found. Click "Auto-Generate" to identify opportunities from your SEU and on-site data.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">ID</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Opportunity</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Category</th>
                      <th className="px-3 py-3 text-right font-semibold text-gray-700">Savings/yr</th>
                      <th className="px-3 py-3 text-right font-semibold text-gray-700">CAPEX</th>
                      <th className="px-3 py-3 text-right font-semibold text-gray-700">Payback</th>
                      <th className="px-3 py-3 text-right font-semibold text-gray-700">CO₂ (t/yr)</th>
                      <th className="px-3 py-3 text-center font-semibold text-gray-700">MAC Class</th>
                      <th className="px-3 py-3 text-center font-semibold text-gray-700">Priority</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredOpportunities.map((opp, idx) => (
                      <tr key={opp.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-3 font-mono text-xs text-gray-500">{opp.opp_id}</td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">{opp.opportunity_title}</div>
                          <div className="text-xs text-gray-500">{opp.system} • {opp.trigger_source}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            opp.opportunity_category === 'No-capex' ? 'bg-green-100 text-green-700' :
                            opp.opportunity_category === 'Low-capex' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {opp.opportunity_category}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-green-600">
                          ₹{((opp.annual_cost_savings || 0) / 100000).toFixed(2)}L
                        </td>
                        <td className="px-3 py-3 text-right text-gray-700">
                          ₹{((opp.capex || 0) / 100000).toFixed(2)}L
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className={`font-semibold ${
                            opp.simple_payback_months < 12 ? 'text-green-600' : 
                            opp.simple_payback_months < 24 ? 'text-amber-600' : 
                            'text-gray-600'
                          }`}>
                            {opp.simple_payback_months === 0 ? 'Immediate' : `${opp.simple_payback_months} mo`}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-purple-600 font-medium">
                          {(opp.annual_co2_reduction || 0).toFixed(1)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            opp.mac_classification === 'No-regret' || opp.classification === 'No-regret' ? 'bg-green-100 text-green-700' :
                            opp.mac_classification === 'Low-cost' || opp.classification === 'Low-cost' ? 'bg-blue-100 text-blue-700' :
                            opp.mac_classification === 'Medium-cost' || opp.classification === 'Medium-cost' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {opp.mac_classification || opp.classification || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            opp.priority_score >= 70 ? 'bg-green-500 text-white' :
                            opp.priority_score >= 50 ? 'bg-blue-500 text-white' :
                            opp.priority_score >= 30 ? 'bg-amber-500 text-white' :
                            'bg-gray-300 text-gray-700'
                          }`}>
                            {opp.priority_score || '-'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MACC Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <button
          onClick={() => toggleSection('macc')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-900">MACC - Marginal Abatement Cost Curve</h2>
            {macc?.summary && (
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                {macc.summary.total_abatement_tco2?.toFixed(0)} tCO₂e potential
              </span>
            )}
          </div>
          {expandedSections.macc ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {expandedSections.macc && macc && (
          <div className="p-6">
            {/* MACC Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-2xl font-bold text-green-700">{macc.summary?.no_regret_count || 0}</p>
                <p className="text-xs text-green-600">No-Regret Opportunities</p>
                <p className="text-xs text-green-500 mt-1">{macc.summary?.no_regret_percent || 0}% of abatement</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{macc.summary?.by_classification?.low_cost || 0}</p>
                <p className="text-xs text-blue-600">Low-Cost (₹0-2K/t)</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-2xl font-bold text-amber-700">{macc.summary?.by_classification?.medium_cost || 0}</p>
                <p className="text-xs text-amber-600">Medium-Cost (₹2-5K/t)</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-2xl font-bold text-red-700">{macc.summary?.by_classification?.high_cost || 0}</p>
                <p className="text-xs text-red-600">High-Cost ({">"} ₹5K/t)</p>
              </div>
            </div>

            {/* MACC Table */}
            {macc.macc_data?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-indigo-50">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold text-indigo-700">Opportunity</th>
                      <th className="px-3 py-3 text-left font-semibold text-indigo-700">Type</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">Lifetime CO₂ (t)</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">CAPEX (₹)</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">Net Savings/yr (₹)</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">Lifetime Net Cost (₹)</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">MAC (₹/tCO₂e)</th>
                      <th className="px-3 py-3 text-center font-semibold text-indigo-700">Classification</th>
                      <th className="px-3 py-3 text-right font-semibold text-indigo-700">Cumulative (t)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {macc.macc_data.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-3 font-medium text-gray-900">{item.opportunity_title}</td>
                        <td className="px-3 py-3 text-gray-600">{item.opportunity_type}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{item.lifetime_co2?.toFixed(0)}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{(item.capex || 0).toLocaleString()}</td>
                        <td className="px-3 py-3 text-right text-green-600 font-medium">{(item.net_annual_savings || 0).toLocaleString()}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{(item.lifetime_net_cost || 0).toLocaleString()}</td>
                        <td className={`px-3 py-3 text-right font-bold ${
                          item.mac_per_tco2 < 0 ? 'text-green-600' :
                          item.mac_per_tco2 <= 2000 ? 'text-blue-600' :
                          item.mac_per_tco2 <= 5000 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {item.mac_per_tco2?.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.classification === 'No-regret' ? 'bg-green-100 text-green-700' :
                            item.classification === 'Low-cost' ? 'bg-blue-100 text-blue-700' :
                            item.classification === 'Medium-cost' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.classification}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-indigo-600 font-medium">{item.cumulative_co2?.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No MACC data available. Generate opportunities first.</p>
            )}
          </div>
        )}
      </div>

      {/* AI Narrative Section */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-md overflow-hidden border border-purple-200">
        <button
          onClick={() => toggleSection('narrative')}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-900">AI-Powered Action Plan & Financial Summary</h2>
          </div>
          {expandedSections.narrative ? <ChevronDown className="w-5 h-5 text-purple-500" /> : <ChevronRight className="w-5 h-5 text-purple-500" />}
        </button>

        {expandedSections.narrative && (
          <div className="p-6">
            {narrative?.narrative ? (
              <div className="prose prose-sm max-w-none">
                {formatNarrative(narrative.narrative)}
              </div>
            ) : (
              <div className="text-center py-6">
                <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Generate an AI-powered analysis with CAPEX prioritization and action plans.</p>
                <button
                  onClick={generateNarrative}
                  disabled={generatingNarrative || opportunities.length === 0}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
                >
                  {generatingNarrative ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generating Analysis...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate AI Analysis
                    </>
                  )}
                </button>
              </div>
            )}
            
            {narrative?.narrative && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={generateNarrative}
                  disabled={generatingNarrative}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingNarrative ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Regenerate
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Opportunity Engine Report</h2>
                <p className="text-green-100 text-sm">{study?.name} • {opportunities.length} Opportunities</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={generateNarrative}
                  disabled={generatingNarrative}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingNarrative ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generatingNarrative ? 'Generating...' : 'AI Analysis'}
                </button>
                <button
                  onClick={printReport}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={downloadPDF}
                  disabled={downloadingPDF}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {downloadingPDF ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  PDF
                </button>
                <button
                  onClick={() => setShowReport(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div ref={reportRef} style={{ wordSpacing: 'normal', letterSpacing: 'normal' }}>
                {/* Report Header */}
                <div className="text-center border-b-2 border-green-600 pb-4 mb-6">
                  <img src="/ergoniq-logo.svg" alt="Ergoniq" className="h-12 mx-auto mb-3" />
                  <h1 style={{ wordSpacing: '0.15em' }} className="text-2xl font-bold text-gray-900">Energy Efficiency Opportunity Report</h1>
                  <p className="text-gray-600">{study?.name}</p>
                  <p className="text-sm text-gray-500">Generated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>

                {/* Executive Summary */}
                <div className="bg-green-50 rounded-lg p-4 mb-6 border border-green-200">
                  <h2 style={{ wordSpacing: '0.15em' }} className="text-lg font-bold text-green-800 mb-3">Executive Summary</h2>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-700">{summary?.total_opportunities || 0}</p>
                      <p className="text-xs text-green-600">Opportunities</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">₹{((summary?.total_annual_savings || 0) / 100000).toFixed(1)}L</p>
                      <p className="text-xs text-green-600">Annual Savings</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">₹{((summary?.total_capex || 0) / 100000).toFixed(1)}L</p>
                      <p className="text-xs text-amber-600">Investment Required</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-700">{(summary?.total_co2_reduction || 0).toFixed(0)} t</p>
                      <p className="text-xs text-purple-600">CO₂e/yr Reduction</p>
                    </div>
                  </div>
                </div>

                {/* Opportunity Register Table */}
                <h2 style={{ wordSpacing: '0.15em' }} className="text-lg font-bold text-gray-900 mb-3">
                  <Lightbulb className="w-5 h-5 text-amber-500 inline-block" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  <span>Opportunity Register</span>
                </h2>
                <table className="w-full text-xs mb-6 border-collapse border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-2 py-2 text-left">ID</th>
                      <th className="border border-gray-300 px-2 py-2 text-left">Opportunity</th>
                      <th className="border border-gray-300 px-2 py-2 text-left">Category</th>
                      <th className="border border-gray-300 px-2 py-2 text-right">Savings (₹/yr)</th>
                      <th className="border border-gray-300 px-2 py-2 text-right">CAPEX (₹)</th>
                      <th className="border border-gray-300 px-2 py-2 text-right">Payback (mo)</th>
                      <th className="border border-gray-300 px-2 py-2 text-right">CO₂ (t/yr)</th>
                      <th className="border border-gray-300 px-2 py-2 text-center">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunities.map((opp, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-2 py-1">{opp.opp_id}</td>
                        <td className="border border-gray-300 px-2 py-1">{opp.opportunity_title}</td>
                        <td className="border border-gray-300 px-2 py-1">{opp.opportunity_category}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{(opp.annual_cost_savings || 0).toLocaleString()}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{(opp.capex || 0).toLocaleString()}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{opp.simple_payback_months || '-'}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{(opp.annual_co2_reduction || 0).toFixed(1)}</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">{opp.priority_score || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* MACC Table */}
                {macc?.macc_data?.length > 0 && (
                  <>
                    <h2 style={{ wordSpacing: '0.15em' }} className="text-lg font-bold text-gray-900 mb-3">
                      <BarChart3 className="w-5 h-5 text-indigo-600 inline-block" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      <span>Marginal Abatement Cost Curve (MACC)</span>
                    </h2>
                    <table className="w-full text-xs mb-6 border-collapse border border-gray-300">
                      <thead className="bg-indigo-50">
                        <tr>
                          <th className="border border-gray-300 px-2 py-2 text-left">Opportunity</th>
                          <th className="border border-gray-300 px-2 py-2 text-right">Lifetime CO₂ (t)</th>
                          <th className="border border-gray-300 px-2 py-2 text-right">CAPEX (₹)</th>
                          <th className="border border-gray-300 px-2 py-2 text-right">Lifetime Net Cost (₹)</th>
                          <th className="border border-gray-300 px-2 py-2 text-right">MAC (₹/tCO₂e)</th>
                          <th className="border border-gray-300 px-2 py-2 text-center">Classification</th>
                          <th className="border border-gray-300 px-2 py-2 text-right">Cumulative (t)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {macc.macc_data.map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-300 px-2 py-1">{item.opportunity_title}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{item.lifetime_co2?.toFixed(0)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{(item.capex || 0).toLocaleString()}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{(item.lifetime_net_cost || 0).toLocaleString()}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{item.mac_per_tco2?.toLocaleString()}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.classification}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right">{item.cumulative_co2?.toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* AI Narrative */}
                {narrative?.narrative && (
                  <>
                    <h2 style={{ wordSpacing: '0.15em' }} className="text-lg font-bold text-gray-900 mb-3">
                      <Sparkles className="w-5 h-5 text-purple-600 inline-block" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      <span>AI-Generated Analysis & Action Plan</span>
                    </h2>
                    <div className="bg-purple-50 rounded-lg p-4 mb-6 border border-purple-200">
                      <div className="prose prose-sm max-w-none">
                        {formatNarrative(narrative.narrative)}
                      </div>
                    </div>
                  </>
                )}

                {/* Footer */}
                <div className="text-center border-t border-gray-300 pt-4 mt-8">
                  <p className="text-xs text-gray-500">
                    © Copyright 2025, Saarv Solutions Private Limited, All rights reserved.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    AI generated reports need to be cross checked.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {opportunities.length} opportunities • Total savings: ₹{((summary?.total_annual_savings || 0) / 100000).toFixed(1)}L/yr
              </p>
              <button
                onClick={() => setShowReport(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
